import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarBlank } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Pick a heatmap cell colour based on cumulative P&L %.
 * Mirrors GitHub-contribution-graph style — discrete intensity buckets so
 * cells stay scannable. Positive = pos green, negative = neg red, zero =
 * neutral surface.
 */
function cellTone(totalPnl, trades) {
  if (!trades) return { bg: "transparent", border: "hsl(var(--subtle))", text: "hsl(var(--muted-fg))" };
  const t = totalPnl;
  if (t >= 50) return { bg: "hsl(var(--signal-positive) / 0.85)", border: "hsl(var(--signal-positive))", text: "hsl(var(--bg))" };
  if (t >= 20) return { bg: "hsl(var(--signal-positive) / 0.55)", border: "hsl(var(--signal-positive) / 0.6)", text: "hsl(var(--bg))" };
  if (t >= 5)  return { bg: "hsl(var(--signal-positive) / 0.28)", border: "hsl(var(--signal-positive) / 0.4)", text: "hsl(var(--signal-positive))" };
  if (t > 0)   return { bg: "hsl(var(--signal-positive) / 0.12)", border: "hsl(var(--signal-positive) / 0.25)", text: "hsl(var(--signal-positive))" };
  if (t > -5)  return { bg: "hsl(var(--signal-negative) / 0.15)", border: "hsl(var(--signal-negative) / 0.3)", text: "hsl(var(--signal-negative))" };
  if (t > -20) return { bg: "hsl(var(--signal-negative) / 0.35)", border: "hsl(var(--signal-negative) / 0.5)", text: "hsl(var(--signal-negative))" };
  return { bg: "hsl(var(--signal-negative) / 0.7)", border: "hsl(var(--signal-negative))", text: "hsl(var(--bg))" };
}

export default function MonthlyHeatmap({ months = 24 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hover, setHover] = useState(null); // { cell, evt }

  useEffect(() => {
    setLoading(true);
    api
      .get(`/backtest/heatmap?months=${months}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("/backtest/heatmap", e);
        setErr(e?.response?.data?.detail || "Failed to load heatmap");
      })
      .finally(() => setLoading(false));
  }, [months]);

  // Build a {year: {month: cell}} index for O(1) lookup while rendering grid.
  const grid = useMemo(() => {
    const g = {};
    (data?.cells || []).forEach((c) => {
      if (!g[c.year]) g[c.year] = {};
      g[c.year][c.month] = c;
    });
    return g;
  }, [data]);

  const years = useMemo(() => {
    if (!data) return [];
    const start = data.start, end = data.end;
    const out = [];
    for (let y = start.year; y <= end.year; y += 1) out.push(y);
    return out;
  }, [data]);

  if (loading) {
    return (
      <div className="text-xs font-mono text-muted-fg py-6" data-testid="heatmap-loading">
        Loading monthly heatmap…
      </div>
    );
  }
  if (err) {
    return (
      <div className="text-xs font-mono text-neg py-6" data-testid="heatmap-error">
        {err}
      </div>
    );
  }
  if (!data || !(data.cells || []).length) {
    return (
      <div className="text-xs font-mono text-muted-fg py-6" data-testid="heatmap-empty">
        No closed trades in this window yet — heatmap will populate as targets / SLs hit.
      </div>
    );
  }

  // Aggregate totals — shown next to the heading for quick scan
  const totals = (data.cells || []).reduce(
    (acc, c) => {
      acc.trades += c.trades;
      acc.wins += c.wins;
      acc.losses += c.losses;
      acc.pnl += c.total_pnl_pct;
      return acc;
    },
    { trades: 0, wins: 0, losses: 0, pnl: 0 },
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border border-subtle bg-surface rounded-sm p-5 relative"
      data-testid="heatmap-card"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CalendarBlank size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Monthly P&amp;L heatmap
            </div>
            {!data.is_premium_view && (
              <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-mod text-mod bg-mod-soft rounded-sm">
                Last 6 mo (free)
              </span>
            )}
          </div>
          <p className="text-xs text-muted-fg max-w-2xl leading-relaxed">
            Cumulative P&amp;L of all CLOSED trades that exited each month
            (target / SL / rule). Greener = higher cumulative gain, redder = drawdown.
            Hover any cell for the full breakdown.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            {data.months_window}-month total
          </div>
          <div
            className={
              "font-mono text-2xl tabular-nums " +
              (totals.pnl >= 0 ? "text-pos" : "text-neg")
            }
            data-testid="heatmap-total"
          >
            {totals.pnl >= 0 ? "+" : ""}
            {totals.pnl.toFixed(1)}%
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            {totals.trades} trades · {totals.wins}W · {totals.losses}L
          </div>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="border-separate" style={{ borderSpacing: 4 }} data-testid="heatmap-grid">
          <thead>
            <tr>
              <th className="text-left text-[9px] font-mono uppercase tracking-widest text-muted-fg pr-2 sticky left-0 bg-surface">
                Year
              </th>
              {MONTH_LABELS.map((m) => (
                <th
                  key={m}
                  className="text-center text-[9px] font-mono uppercase tracking-widest text-muted-fg w-12"
                >
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {years.map((y) => (
              <tr key={y}>
                <td className="text-right pr-2 text-[10px] font-mono uppercase tracking-widest text-secondary-fg sticky left-0 bg-surface">
                  {y}
                </td>
                {MONTH_LABELS.map((_, i) => {
                  const month = i + 1;
                  const cell = grid[y]?.[month];
                  const tone = cellTone(cell?.total_pnl_pct ?? 0, cell?.trades ?? 0);
                  const isFuture =
                    y > data.end.year || (y === data.end.year && month > data.end.month);
                  const isBeforeWindow =
                    y < data.start.year || (y === data.start.year && month < data.start.month);
                  const greyed = isFuture || isBeforeWindow;
                  return (
                    <td key={`${y}-${month}`} className="text-center">
                      <div
                        role={cell ? "button" : undefined}
                        tabIndex={cell ? 0 : -1}
                        onMouseEnter={(e) =>
                          cell && setHover({ cell, x: e.currentTarget.getBoundingClientRect() })
                        }
                        onMouseLeave={() => setHover(null)}
                        onFocus={(e) =>
                          cell && setHover({ cell, x: e.currentTarget.getBoundingClientRect() })
                        }
                        onBlur={() => setHover(null)}
                        data-testid={`heatmap-cell-${y}-${month}`}
                        className={
                          "w-12 h-10 mx-auto rounded-sm border flex flex-col items-center justify-center font-mono transition " +
                          (cell ? "cursor-default hover:scale-105" : "") +
                          (greyed ? " opacity-30" : "")
                        }
                        style={{
                          backgroundColor: tone.bg,
                          borderColor: tone.border,
                          color: tone.text,
                        }}
                      >
                        {cell ? (
                          <>
                            <div className="text-[10px] tabular-nums leading-tight">
                              {cell.total_pnl_pct >= 0 ? "+" : ""}
                              {cell.total_pnl_pct.toFixed(0)}%
                            </div>
                            <div className="text-[8px] tabular-nums opacity-80 leading-tight">
                              {cell.trades}t
                            </div>
                          </>
                        ) : (
                          <span className="text-[9px] opacity-50">·</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <span>Less</span>
        {[-25, -10, 0, 5, 15, 35, 60].map((v) => {
          const t = cellTone(v, 1);
          return (
            <span
              key={v}
              className="w-5 h-3 rounded-sm border inline-block"
              style={{ backgroundColor: t.bg, borderColor: t.border }}
              title={`~${v}% cumulative`}
            />
          );
        })}
        <span>More</span>
        <span className="ml-3 text-muted-fg/70">
          · Numbers in cell: cumulative %, trade count
        </span>
      </div>

      {/* Tooltip */}
      {hover && (
        <div
          className="pointer-events-none fixed z-50 p-3 border border-pos/60 bg-app rounded-sm shadow-xl text-xs font-mono"
          style={{
            top: Math.max(8, hover.x.bottom + 6),
            left: Math.min(
              (typeof window !== "undefined" ? window.innerWidth : 1200) - 240,
              Math.max(8, hover.x.left - 24),
            ),
            minWidth: 220,
          }}
          data-testid="heatmap-tooltip"
        >
          <div className="text-[10px] uppercase tracking-widest text-pos mb-1">
            {MONTH_LABELS[hover.cell.month - 1]} {hover.cell.year}
          </div>
          <div className="space-y-0.5 text-secondary-fg">
            <div>
              Trades:{" "}
              <span className="text-primary-fg tabular-nums">{hover.cell.trades}</span>
            </div>
            <div>
              Wins:{" "}
              <span className="text-pos tabular-nums">{hover.cell.wins}</span> · Losses:{" "}
              <span className="text-neg tabular-nums">{hover.cell.losses}</span>
              {hover.cell.exits ? (
                <>
                  {" "}· Exits:{" "}
                  <span className="text-mod tabular-nums">{hover.cell.exits}</span>
                </>
              ) : null}
            </div>
            <div>
              Win rate:{" "}
              <span className="text-primary-fg tabular-nums">{hover.cell.win_rate}%</span>
            </div>
            <div>
              Avg P&amp;L:{" "}
              <span
                className={
                  (hover.cell.avg_pnl_pct >= 0 ? "text-pos" : "text-neg") + " tabular-nums"
                }
              >
                {hover.cell.avg_pnl_pct >= 0 ? "+" : ""}
                {hover.cell.avg_pnl_pct}%
              </span>
            </div>
            <div className="pt-1 border-t border-subtle mt-1">
              Cumulative:{" "}
              <span
                className={
                  (hover.cell.total_pnl_pct >= 0 ? "text-pos" : "text-neg") +
                  " tabular-nums font-semibold"
                }
              >
                {hover.cell.total_pnl_pct >= 0 ? "+" : ""}
                {hover.cell.total_pnl_pct}%
              </span>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
